import { UseFormReturn } from '@graphcommerce/ecommerce-ui'
import {
  CustomizableDropDownValue,
  CustomizableProductInterface,
} from '@graphcommerce/graphql-mesh'
import { Money } from '@graphcommerce/magento-store'
import { filterNonNullableKeys } from '@graphcommerce/next-ui'
import React, { useCallback, useEffect, useMemo } from 'react'
import { OptionTypeRenderer } from '../../Product/ProductCustomizable/ProductCustomizable'

export const TurnAroundTimeDropDownOption = React.memo<
  React.ComponentProps<OptionTypeRenderer['CustomizableDropDownOption']> & {
    form: UseFormReturn<any>
    selectedVariantSku?: string | null
    product: Pick<CustomizableProductInterface, 'options'>
    colorSpecOptionValue?: CustomizableDropDownValue
  }
>((props) => {
  const { uid, required, optionIndex, index, title, dropdownValue, form, colorSpecOptionValue } =
    props
  const { setValue, getFieldState } = form

  const { watch } = form

  const enteredOptions = watch('enteredOptions')
  const quantity = watch('quantity')

  const allowedOptions = useMemo(() => {
    if (!dropdownValue || !enteredOptions) {
      return []
    }

    return dropdownValue
      .filter((v) => {
        const value = v as CustomizableDropDownValue

        if (
          !value.fourover_data?.conditions?.colorSpec ||
          !colorSpecOptionValue ||
          // We keep runsize with quantity, so we need to split before compare
          colorSpecOptionValue?.title?.split(' - ').at(0) !==
            value.fourover_data?.conditions?.colorSpec
        ) {
          return false
        }

        if (
          !value.fourover_data?.conditions?.runSize ||
          !quantity ||
          quantity !== value.fourover_data?.conditions?.runSize
        ) {
          return false
        }
        return true
      })
      .map((v) => {
        const value = v as CustomizableDropDownValue
        if (!value.title?.includes('Business Day') && value.title?.includes('Day')) {
          const dayParts = value.title.split(' ')
          const dayValue = dayParts?.at(0)
          return { ...value, title: `${dayValue} Business Days` }
        }
        return value
      })
  }, [dropdownValue, enteredOptions, quantity, colorSpecOptionValue])

  const handleOptionSelect = useCallback(
    (newValue: string) => {
      const entered = Array.isArray(enteredOptions)
        ? [...enteredOptions].filter((item) => item)
        : []
      if (entered.find((o) => o.uid === uid)) {
        entered.find((o) => o.uid === uid).value = newValue
      } else {
        entered.push({ uid, value: newValue })
      }
      setValue(`enteredOptions`, entered, {})
    },
    [enteredOptions, uid, setValue],
  )

  const selectedValue = enteredOptions ? enteredOptions.find((o) => o?.uid === uid)?.value : ''

  useEffect(() => {
    // Auto-select the first option
    const selectedOption = allowedOptions?.find((o) => o && o?.uid?.toString() === selectedValue)
    if (!selectedOption) {
      const cheapest = allowedOptions.length
        ? allowedOptions.reduce((a, b) => ((a?.price || 0) < (b?.price || 0) ? a : b))
        : null
      if (cheapest) {
        handleOptionSelect(cheapest?.uid?.toString() || '')
      }
    }
  }, [selectedValue, uid, handleOptionSelect, allowedOptions, quantity])

  if (!colorSpecOptionValue) {
    return null
  }

  const isThreeColTrigger =
    allowedOptions.length > 12 || (title?.toLowerCase().includes('size') ?? false)

  return (
    <div className='my-3.75'>
      <p className='mb-3 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
        {title}
      </p>
      <div className={isThreeColTrigger ? 'grid grid-cols-1 gap-2 lg:grid-cols-3' : 'grid grid-cols-1 gap-2 lg:grid-cols-2'}>
        {filterNonNullableKeys(allowedOptions, ['title']).map((option) => {
          const isSelected = selectedValue === option.uid?.toString()
          return (
            <button
              key={option.uid}
              type='button'
              onClick={() => {
                if (uid && option.uid) {
                  handleOptionSelect(option.uid.toString())
                }
              }}
              className={`w-full overflow-hidden rounded-[4px] px-2.5 py-2.5 transition-all active:scale-[0.97] ${
                isSelected
                  ? 'bg-[#D1CBCB33] text-black border-[1.6px] border-[#5D54DE] shadow-[0px_2px_3px_0px_rgba(0,0,0,0.25)]'
                  : 'bg-[#F8F8F8] text-black border-[1.4px] border-[#908B8B]'
              }`}
            >
              <span className={`block truncate text-[13px] uppercase tracking-[0.04em] ${isSelected ? 'font-bold' : 'font-medium'}`}>
                {option.title}
                {!!option.price && option.price > 0 && (
                  <> +<Money value={option.price} /></>
                )}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
})
